import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Brain, Database, Network, ArrowRight, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';

const Technology = () => {
  const [activeTab, setActiveTab] = useState(0);

  const technologies = [
    {
      icon: <Brain size={28} />,
      title: "Advanced ML Models",
      description: "Proprietary ML Models trained to transform complex oncology data into actionable insights.",
      features: [
        "Deep learning neural networks",
        "Predictive treatment modeling",
        "Continuous learning capabilities",
        "Real-time pattern recognition"
      ],
      color: "#3b82f6"
    },
    {
      icon: <Database size={28} />,
      title: "Integrated Data Platform",
      description: "Seamless integration of genetic, proteomic, and clinical data into one secure platform that fits directly into hospital workflows.",
      features: [
        "Unified patient profiles combining clinical and multi-omics data",
        "Interoperable design for smooth hospital integration",
        "Strong cryptography and anonymization safeguards",
        "Compliance with GDPR, HIPAA, MDR, and more"
      ],
      color: "#8b5cf6"
    },
    {
      icon: <Network size={28} />,
      title: "Clinical Decision Support",
      description: "Evidence-based recommendations designed to support oncologists in daily decision-making.",
      features: [
        "Optimized treatment pathways for each patient",
        "Risk stratification and outcome prediction",
        "Built-in alignment with clinical guidelines",
        "Clear, explainable insights at the point of care"
      ],
      color: "#06b6d4"
    }
  ];

  return (
    <Section id="technology">
      <BackgroundElements>
        <FloatingOrb style={{ top: '10%', left: '10%' }} />
        <FloatingOrb style={{ top: '70%', right: '15%' }} />
        <GridPattern />
      </BackgroundElements>
      
      <ContentWrapper>
        {/* Header Section */}
        <HeaderSection>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <TechLabel>Our Technology</TechLabel>
            <SectionTitle>
              Powered by <GradientText>Evidence-based</GradientText> Guidelines
            </SectionTitle>
            <SectionSubtitle>
             Our platform combines ML, comprehensive health data, and clinical insight to redefine decision-making in late-stage cancer treatment.
            </SectionSubtitle>
          </motion.div>
        </HeaderSection>

        {/* Interactive Technology Carousel */}
        <TechnologySection>
          <CarouselContainer>
            <CarouselButton 
              onClick={() => setActiveTab(activeTab === 0 ? technologies.length - 1 : activeTab - 1)}
              position="left"
            >
              <ChevronLeft size={24} />
            </CarouselButton>
            
            <CarouselWrapper>
              {/* Left Side Card */}
              {activeTab > 0 && (
                <SideCard onClick={() => setActiveTab(activeTab - 1)}>
                  <TechCard active={false}>
                    <TechCardHeader>
                      <TechIcon style={{ backgroundColor: `${technologies[activeTab - 1].color}15`, color: technologies[activeTab - 1].color }}>
                        {technologies[activeTab - 1].icon}
                      </TechIcon>
                      <div>
                        <TechCardTitle>{technologies[activeTab - 1].title}</TechCardTitle>
                        <TechCardDescription>{technologies[activeTab - 1].description}</TechCardDescription>
                      </div>
                    </TechCardHeader>
                  </TechCard>
                </SideCard>
              )}
              
              {/* Main Active Card */}
              <MainCard>
                <TechCard active={true}>
                  <TechCardHeader>
                    <TechIcon style={{ backgroundColor: `${technologies[activeTab].color}15`, color: technologies[activeTab].color }}>
                      {technologies[activeTab].icon}
                    </TechIcon>
                    <div>
                      <TechCardTitle>{technologies[activeTab].title}</TechCardTitle>
                      <TechCardDescription>{technologies[activeTab].description}</TechCardDescription>
                    </div>
                  </TechCardHeader>
                  
                  <FeaturesList>
                    {technologies[activeTab].features.map((feature, featureIndex) => (
                      <FeatureItem
                        key={featureIndex}
                        as={motion.div}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: featureIndex * 0.1 }}
                      >
                        <CheckCircle size={16} style={{ color: technologies[activeTab].color }} />
                        {feature}
                      </FeatureItem>
                    ))}
                  </FeaturesList>
                </TechCard>
              </MainCard>
              
              {/* Right Side Card */}
              {activeTab < technologies.length - 1 && (
                <SideCard onClick={() => setActiveTab(activeTab + 1)}>
                  <TechCard active={false}>
                    <TechCardHeader>
                      <TechIcon style={{ backgroundColor: `${technologies[activeTab + 1].color}15`, color: technologies[activeTab + 1].color }}>
                        {technologies[activeTab + 1].icon}
                      </TechIcon>
                      <div>
                        <TechCardTitle>{technologies[activeTab + 1].title}</TechCardTitle>
                        <TechCardDescription>{technologies[activeTab + 1].description}</TechCardDescription>
                      </div>
                    </TechCardHeader>
                  </TechCard>
                </SideCard>
              )}
            </CarouselWrapper>
            
            <CarouselButton 
              onClick={() => setActiveTab(activeTab === technologies.length - 1 ? 0 : activeTab + 1)}
              position="right"
            >
              <ChevronRight size={24} />
            </CarouselButton>
          </CarouselContainer>
          
          <CarouselIndicators>
            {technologies.map((_, index) => (
              <Indicator 
                key={index}
                active={activeTab === index}
                onClick={() => setActiveTab(index)}
                style={{ backgroundColor: activeTab === index ? technologies[activeTab].color : '#e2e8f0' }}
              />
            ))}
          </CarouselIndicators>
        </TechnologySection>
      </ContentWrapper>
    </Section>
  );
};

const Section = styled.section`
  padding: 80px 0;
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
  position: relative;
  overflow: hidden;
`;

const BackgroundElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
`;

const FloatingOrb = styled.div`
  position: absolute;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%);
  filter: blur(40px);
  animation: float 6s ease-in-out infinite;
  
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-20px); }
  }
`;

const GridPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    linear-gradient(rgba(0, 0, 0, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 0, 0, 0.05) 1px, transparent 1px);
  background-size: 50px 50px;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 2;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 60px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -30px;
    left: 50%;
    transform: translateX(-50%);
    width: 120px;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.4), transparent);
  }
`;

const TechLabel = styled.div`
  display: inline-block;
  color: #3b82f6;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 16px;
  text-transform: uppercase;
  letter-spacing: 1px;
  opacity: 0.8;
`;

const SectionTitle = styled.h2`
  font-size: 2.8rem;
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 24px;
  color: #1a1a1a;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const GradientText = styled.span`
  background: linear-gradient(135deg, #2094F1 0%, #856AC3 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SectionSubtitle = styled.p`
  font-size: 1.3rem;
  line-height: 1.7;
  color: #64748b;
  max-width: 800px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const TechnologySection = styled.div`
  margin-bottom: 80px;
  position: relative;
  padding-top: 40px;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 150px;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(139, 92, 246, 0.3), transparent);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -40px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(6, 182, 212, 0.3), transparent);
  }
`;

const TechTabs = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 60px;
  flex-wrap: wrap;
`;

const TabButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border: 2px solid ${props => props.active ? 'currentColor' : 'transparent'};
  background: ${props => props.active ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.5)'};
  border-radius: 50px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  
  &:hover {
    background: rgba(255, 255, 255, 0.9);
    transform: translateY(-2px);
  }
`;

const CarouselContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin-bottom: 30px;
  position: relative;
`;

const CarouselWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 20px;
  position: relative;
  width: 100%;
  max-width: 1200px;
  
  /* Add gradient fade overlay */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      rgba(248, 250, 252, 0.7) 0%,
      rgba(248, 250, 252, 0.3) 20%,
      transparent 40%,
      transparent 60%,
      rgba(248, 250, 252, 0.3) 80%,
      rgba(248, 250, 252, 0.7) 100%
    );
    pointer-events: none;
    z-index: 3;
    border-radius: 20px;
  }
  
  @media (max-width: 768px) {
    justify-content: center;
    
    &::before {
      display: none;
    }
  }
`;

const MainCard = styled.div`
  flex: 0 0 auto;
  width: 580px;
  max-width: 580px;
  z-index: 4;
  position: relative;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  
  @media (max-width: 1200px) {
    width: 500px;
    max-width: 500px;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    max-width: 500px;
  }
`;

const SideCard = styled.div`
  flex: 0 0 auto;
  width: 300px;
  opacity: 1;
  transform: scale(0.85);
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  z-index: 2;
  position: relative;
  
  &:hover {
    transform: scale(0.9);
  }
  
  @media (max-width: 1200px) {
    width: 250px;
    transform: scale(0.8);
    
    &:hover {
      transform: scale(0.85);
    }
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const CarouselSlide = styled.div`
  flex: 0 0 100%;
  width: 100%;
  padding: 0 60px;
  opacity: ${props => props.active ? 1 : 0.5};
  transform: ${props => props.active ? 'scale(1)' : 'scale(0.85)'};
  transition: all 0.5s ease-in-out;
  
  @media (max-width: 768px) {
    padding: 0 30px;
    transform: ${props => props.active ? 'scale(1)' : 'scale(0.9)'};
  }
  
  @media (max-width: 480px) {
    padding: 0 15px;
  }
`;

const CarouselButton = styled.button`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #64748b;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  z-index: 4;
  
  &:hover {
    background: rgba(255, 255, 255, 1);
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
    color: #3b82f6;
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  @media (max-width: 768px) {
    width: 45px;
    height: 45px;
  }
`;

const CarouselIndicators = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 30px;
`;

const Indicator = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.2);
  }
`;

const TechContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const TechCard = styled.div`
  background: ${props => props.active ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.8)'};
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 32px;
  border: 1px solid ${props => props.active ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.2)'};
  box-shadow: ${props => props.active ? '0 16px 48px rgba(0, 0, 0, 0.1)' : '0 6px 24px rgba(0, 0, 0, 0.06)'};
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  height: 400px;
  display: flex;
  flex-direction: column;
  width: 100%;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.5), rgba(139, 92, 246, 0.5), transparent);
    transition: left 0.6s ease;
  }
  
  &:hover::before {
    left: ${props => props.active ? '100%' : '0'};
  }
`;

const TechCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 18px;
  margin-bottom: 28px;
  flex-shrink: 0;
`;

const TechIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  svg {
    width: 28px;
    height: 28px;
  }
  
  @media (max-width: 1200px) {
    width: 50px;
    height: 50px;
    
    svg {
      width: 24px;
      height: 24px;
    }
  }
`;

const TechCardTitle = styled.h3`
  font-size: 1.6rem;
  font-weight: 700;
  margin-bottom: 6px;
  color: #1a1a1a;
  line-height: 1.2;
  
  @media (max-width: 1200px) {
    font-size: 1.4rem;
  }
`;

const TechCardDescription = styled.p`
  font-size: 0.95rem;
  color: #64748b;
  line-height: 1.4;
  
  @media (max-width: 1200px) {
    font-size: 0.9rem;
  }
`;

const FeaturesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1rem;
  color: #374151;
  font-weight: 500;
`;

const CapabilitiesSection = styled.div`
  margin-bottom: 80px;
  position: relative;
  padding-top: 50px;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 180px;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(6, 182, 212, 0.4), rgba(139, 92, 246, 0.4), transparent);
  }
`;

const CapabilitiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
`;

const CapabilityCard = styled.div`
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  padding: 30px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
  text-align: center;
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.3), rgba(139, 92, 246, 0.3), transparent);
    transform: scaleX(0);
    transition: transform 0.4s ease;
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 16px 48px rgba(0, 0, 0, 0.1);
    background: rgba(255, 255, 255, 0.9);
    
    &::before {
      transform: scaleX(1);
    }
  }
`;

const CapabilityIcon = styled.div`
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin: 0 auto 20px;
`;

const CapabilityTitle = styled.h4`
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 8px;
  color: #1a1a1a;
`;

const CapabilityDescription = styled.p`
  color: #64748b;
  font-size: 1rem;
`;

export default Technology;